/* eslint-disable @next/next/no-img-element */
import { css } from 'goober';
import { ShoppingCart } from 'lucide-react';

const cardStyle = css`
    display: flex;
    flex-direction: column;
    width: 100%;
    background: #fff;
    padding: 12px;
    border-radius: 12px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    border: 1px solid #eee;
    transition:
        transform 0.2s ease,
        box-shadow 0.2s ease;
    height: 100%;

    @media (hover: hover) and (pointer: fine) {
        &:hover {
            transform: translateY(-2px);
            box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
        }
    }
`;

const imageStyle = css`
    width: 100%;
    aspect-ratio: 1;
    height: auto;
    object-fit: cover;
    border-radius: 8px;
    background: #f4f4f4;
    border: 1px solid var(--bb-border-color);
    margin-bottom: 12px;

    @media (max-width: 768px) {
        --size: 140px;
    }
`;

const contentStyle = css`
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
`;

const titleStyle = css`
    font-weight: 600;
    font-size: 15px;
    color: #2e0a57;
    line-height: 1.4;
    margin-bottom: 8px;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    min-height: 2.8em;
    text-decoration: none;
    transition: color 0.2s ease;

    /* Only enable hover on non-touch devices */
    @media (hover: hover) and (pointer: fine) {
        &:hover {
            color: #1a0635;
            text-decoration: underline;
        }
    }
`;

const priceStyle = css`
    font-size: 17px;
    font-weight: 600;
    color: var(--bb-primary, #2e0a57);
    margin-bottom: 12px;
`;

const actionsStyle = css`
    display: flex;
    flex-direction: column;
    gap: 6px;
    margin-top: auto;
`;

const button = css`
    border: none;
    background: var(--bb-primary, #2e0a57);
    color: white;
    font-size: 13px;
    font-weight: 500;
    padding: 10px 16px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    gap: 8px;
    justify-content: center;
    cursor: pointer;
    text-decoration: none;
    transition: all 0.2s ease;
    white-space: nowrap;
    position: relative;
    overflow: hidden;
    box-shadow: 0 2px 4px rgba(46, 10, 87, 0.2);

    &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: rgba(255, 255, 255, 0.1);
        transform: translateX(-100%);
        transition: transform 0.2s ease;
    }

    /* Only enable hover effects on devices that can hover */
    @media (hover: hover) and (pointer: fine) {
        &:hover {
            transform: translateY(-1px);
            box-shadow: 0 4px 8px rgba(46, 10, 87, 0.3);

            &::before {
                transform: translateX(0);
            }
        }
    }

    /* Active state */
    &:active {
        transform: translateY(0);
        box-shadow: 0 1px 2px rgba(46, 10, 87, 0.2);
        background: rgba(46, 10, 87, 0.9);
    }
`;

const buttonFilled = css`
    /* Button is already filled with gradient, no additional styles needed */
`;

export interface IProductItem {
    id?: string | number;
    title: string;
    image: string;
    price: string;
    url: string;
    variant_id?: string | number;
    quantity?: number;
}

interface IProps {
    product: IProductItem;
    onChange: (product: IProductItem, type: 'view' | 'addToCart') => void;
}

export default function ProductCard({ product, onChange }: IProps) {
    const handleAddToCart = async (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        onChange(product, 'addToCart');

        // Single responsibility: trigger the cart service with the event bus
        // await CartService.addToCart(product, eventBus);
    };

    const handleViewDetails = () => {
        onChange(product, 'view');
        // Don't prevent default to allow link opening in new tab
        // e.stopPropagation();
        // // Emit event for viewing product details
        // eventBus.emit(EventType.PRODUCT_VIEW_DETAILS, {
        //     product: {
        //         ...product,
        //         quantity: product.quantity || 1
        //     }
        // });
    };

    return (
        <div className={cardStyle}>
            <img
                src={product.image}
                alt={product.title}
                className={imageStyle}
            />
            <div className={contentStyle}>
                <div>
                    <a
                        href={product.url}
                        className={titleStyle}
                        target='_blank'
                        rel='noopener noreferrer'
                        onClick={handleViewDetails}
                    >
                        {product.title}
                    </a>
                    <div className={priceStyle}>${product.price}</div>
                </div>
                <div className={actionsStyle}>
                    <button
                        type='button'
                        onClick={handleAddToCart}
                        className={`${button} ${buttonFilled}`}
                    >
                        <ShoppingCart size={14} /> Add to Cart
                    </button>
                </div>
            </div>
        </div>
    );
}
